import type { EffectiveAccess } from "../services/authService";
import type { ContentRiskLevel } from "../types/aiSuggestion";
import { parseContentRiskLevel } from "./aiSuggestionDisplay";
import { hasSuperAdminRole } from "./sessionModuleAccess";

const ORDER: readonly ContentRiskLevel[] = [
  "LegalDecision",
  "BusinessDecision",
  "ExecDecision",
];

/** All risk tiers — used for browsing suggestions (visibility is not role-gated). */
export const ALL_CONTENT_RISK_LEVELS: ReadonlySet<ContentRiskLevel> = new Set(
  ORDER,
);

/** Highest-precedence known decision role from verify (`effective_access.decision_role_code`). */
export function normalizeDecisionRoleCode(
  raw: string | null | undefined,
): "RED" | "AMBER" | "GREEN" | null {
  if (raw == null || raw === "") {
    return null;
  }
  const u = raw.trim().toUpperCase();
  if (u === "YELLOW") {
    return "AMBER";
  }
  if (u === "RED" || u === "AMBER" || u === "GREEN") {
    return u;
  }
  return null;
}

/** Default pill when all tiers are visible (Red → Amber → Green). */
export function defaultViewContentRiskLevel(): ContentRiskLevel {
  return "LegalDecision";
}

/** Risk tiers the user may approve, reject, or restore; unknown/null ⇒ Green (`ExecDecision`) only. */
export function allowedContentRiskLevelsForDecisionRole(
  code: "RED" | "AMBER" | "GREEN" | null,
): ReadonlySet<ContentRiskLevel> {
  switch (code) {
    case "RED":
      return new Set<ContentRiskLevel>(ORDER);
    case "AMBER":
      return new Set<ContentRiskLevel>(["BusinessDecision", "ExecDecision"]);
    case "GREEN":
      return new Set<ContentRiskLevel>(["ExecDecision"]);
    default:
      return new Set<ContentRiskLevel>(["ExecDecision"]);
  }
}

/** Actionable tiers for approve / reject / restore from session `effective_access`. */
export function allowedLevelsFromEffectiveAccess(
  access: EffectiveAccess | null,
): ReadonlySet<ContentRiskLevel> {
  if (access && hasSuperAdminRole(access.admin_roles ?? [])) {
    return ALL_CONTENT_RISK_LEVELS;
  }
  return allowedContentRiskLevelsForDecisionRole(
    normalizeDecisionRoleCode(access?.decision_role_code ?? null),
  );
}

/** Whether the user may approve, reject, or undo this suggestion's risk tier. */
export function canActOnSuggestionRiskLevel(
  access: EffectiveAccess | null,
  suggestionRisk: string | null | undefined,
): boolean {
  const level = parseContentRiskLevel(suggestionRisk);
  if (!level) {
    return false;
  }
  return allowedLevelsFromEffectiveAccess(access).has(level);
}

/** First tier in Red → Amber → Green order that the user may act on. */
export function firstAllowedContentRiskLevel(
  allowed: ReadonlySet<ContentRiskLevel>,
): ContentRiskLevel {
  for (const level of ORDER) {
    if (allowed.has(level)) {
      return level;
    }
  }
  return "ExecDecision";
}
